import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Award, BookOpen, Clock, Target } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#1A4D2E', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B'];

const ProgressPage = () => {
  const [progress, setProgress] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [progressRes, statsRes] = await Promise.all([
        axios.get(`${API}/progress`),
        axios.get(`${API}/dashboard/stats`)
      ]);
      setProgress(progressRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading progress...</div>;
  }

  const subjectData = progress?.subject_progress || [];
  const totalStudyMinutes = subjectData.reduce((acc, s) => acc + (s.study_minutes || 0), 0);

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="progress-page">
      <div>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">Progress</h1>
        <p className="text-muted-foreground mt-2 text-base sm:text-lg leading-relaxed">
          Track your learning journey and achievements
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <Card className="p-4 sm:p-6 rounded-sm border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Lectures</p>
                <p className="text-2xl font-bold">{stats?.total_lectures || 0}</p>
              </div>
            </div>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          <Card className="p-4 sm:p-6 rounded-sm border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                <Target className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Subjects</p>
                <p className="text-2xl font-bold">{stats?.total_subjects || 0}</p>
              </div>
            </div>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.2 }}>
          <Card className="p-4 sm:p-6 rounded-sm border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-green-100 dark:bg-green-900 flex items-center justify-center">
                <Clock className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Study Hours</p>
                <p className="text-2xl font-bold">{Math.round(totalStudyMinutes / 60)}</p>
              </div>
            </div>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.3 }}>
          <Card className="p-4 sm:p-6 rounded-sm border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-orange-100 dark:bg-orange-900 flex items-center justify-center">
                <Award className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Streak</p>
                <p className="text-2xl font-bold">{progress?.study_streak || 0} days</p>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.4 }}>
          <Card className="p-4 sm:p-6 rounded-sm border border-border">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp className="w-5 h-5 text-primary" />
              <h2 className="text-xl sm:text-2xl font-medium">Lectures per Subject</h2>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={subjectData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="subject_name" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="lectures" fill="#1A4D2E" name="Lectures" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.5 }}>
          <Card className="p-4 sm:p-6 rounded-sm border border-border">
            <div className="flex items-center gap-2 mb-6">
              <Target className="w-5 h-5 text-primary" />
              <h2 className="text-xl sm:text-2xl font-medium">Learning Bytes Distribution</h2>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={subjectData}
                  dataKey="learning_bytes"
                  nameKey="subject_name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                >
                  {subjectData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.6 }}>
        <Card className="p-4 sm:p-6 rounded-sm border border-border">
          <h2 className="text-xl sm:text-2xl font-medium mb-6">Subject Progress Details</h2>
          <div className="space-y-4">
            {subjectData.map((subject, index) => (
              <div key={index} className="p-4 rounded-sm border border-border hover:bg-muted/50 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-medium text-lg">{subject.subject_name}</h3>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                      <span>{subject.lectures} lectures</span>
                      <span>•</span>
                      <span>{subject.learning_bytes} learning bytes</span>
                      <span>•</span>
                      <span>{Math.round((subject.study_minutes || 0) / 60)}h study time</span>
                    </div>
                  </div>
                  <div className="w-full sm:w-32">
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{ width: `${Math.min(100, (subject.learning_bytes / 10) * 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 text-right">
                      {Math.min(100, Math.round((subject.learning_bytes / 10) * 100))}% mastered
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </motion.div>
    </div>
  );
};

export default ProgressPage;